import { css } from 'aphrodite';
import * as React from 'react';
import { Pill } from '../../../../../../../../web/components-v2/Pill/Pill';
import { bs } from '../../../../../../../../web/styles/styles';
import { IEventTicket } from '../../../../../../../models/Api';
import { styleSheet } from '../styles';
import { LegacyPaymentSummary } from './LegacyPaymentSummary';

interface PaymentSummaryProps {
	ticketSelections?: Record<string, number>;
	tickets?: IEventTicket[];
	ticketsOrder: number;
	paidTickets: number;
	formattedAmount: string | number;
	getPaidTicketQuantities: () => Record<string, number>;
	isLegacyEvent?: boolean; // Uses costPerAttendee instead of tiered tickets
	isPaidEvent?: boolean;
	/** Cost per attendee in cents (required for legacy events) */
	costPerAttendee?: number;
}

export const PaymentSummary: React.FC<PaymentSummaryProps> = ({
	ticketSelections,
	tickets,
	ticketsOrder,
	paidTickets,
	formattedAmount,
	getPaidTicketQuantities,
	isLegacyEvent = false,
	isPaidEvent = false,
	costPerAttendee,
}) => {
	if (!isPaidEvent || ticketsOrder === 0) {
		return null;
	}

	return (
		<div className={css(styleSheet.paymentInfo)}>
			<div className={css(styleSheet.divider)} />
			{/* Tiered ticketing: show breakdown by ticket type */}
			{tickets && tickets.length > 0 && !isLegacyEvent ? (
				<>
					<section>
						{Object.entries(ticketSelections || {}).map(([ticketId, quantity]) => {
							if (quantity === 0) return null;
							const ticket = tickets.find(t => t.id === ticketId);
							if (!ticket) return null;

							// Calculate unpaid quantity for display (subtract already paid tickets)
							const paidQuantities = getPaidTicketQuantities();
							const alreadyPaidQuantity = paidQuantities[ticketId] || 0;
							const unpaidQuantity = quantity - alreadyPaidQuantity;

							// Only show line item if there are unpaid tickets
							if (unpaidQuantity <= 0) return null;

							const ticketTotal = (ticket.unitPrice || 0) * unpaidQuantity;
							const formattedTicketTotal = new Intl.NumberFormat('en-US', {
								style: 'currency',
								currency: 'USD',
							}).format(ticketTotal / 100);

							return (
								<div key={ticketId} className={css(bs.flex, bs.justifyBetween, bs.itemsCenter, bs.mb2)}>
									<p>
										{ticket.name} × {unpaidQuantity}
									</p>
									<p>
										<span className={css(styleSheet?.priceAmount)}>{formattedTicketTotal}</span>
									</p>
								</div>
							);
						})}
					</section>
				</>
			) : null}

			{/* Legacy: simple ticket count (only for costPerAttendee events) */}
			{isLegacyEvent ? (
				costPerAttendee ? (
					<LegacyPaymentSummary
						ticketsOrder={ticketsOrder}
						paidTickets={paidTickets}
						costPerAttendee={costPerAttendee}
					/>
				) : (
					// Fallback to original display if costPerAttendee is missing (backward compatibility)
					<section className={css(bs.flex, bs.gap2, bs.itemsCenter)}>
						<p>
							Tickets × <span className={css(styleSheet?.ticketCountText)}>{ticketsOrder}</span>
						</p>
						{paidTickets ? (
							<div className={css(styleSheet.paidTickets)}>
								<Pill label={`Paid Tickets: ${paidTickets}`} variant='label' color={'green'} />
							</div>
						) : null}
					</section>
				)
			) : null}
			<p className={css(styleSheet?.paymentInfoText)}>
				<strong>Total</strong> <strong>{formattedAmount}</strong>
			</p>
			<span className={css(styleSheet?.taxNote)}>Taxes are calculated at checkout</span>
		</div>
	);
};
